import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OrderQuantitySelector } from "./checkout/OrderQuantitySelector";
import { getContrastTextColor } from "@/utils/colorUtils";
import { formatCentsAsUsd } from "@/lib/money";
import { toast } from "sonner";

interface DisplayProduct {
  _id: string;
  /** Product title from Product.name */
  name?: string;
  /** Variant display name when line has variantId */
  variantName?: string;
  price: number;
  images?: { url: string; thumbnailUrl?: string }[];
  payWhatYouCan?: boolean;
  quantityRules?: { minimum: number; maximum: number };
}

export function PaymentRoute() {
  const { id: paymentId } = useParams<{ id: string }>();
  const { setCheckoutSettings } = usePage();
  const [artistName, setArtistName] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [products, setProducts] = useState<DisplayProduct[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [checkoutSettings, setLocalCheckoutSettings] = useState<{
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const noopQuantity = useCallback(() => {}, []);

  useEffect(() => {
    if (!paymentId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`/api/payments/${paymentId}`);
        if (!res.ok) {
          if (res.status === 404) setNotFound(true);
          return;
        }
        const data = (await res.json()) as {
          artistName?: string;
          payment?: { status?: string; amount?: number };
          products?: DisplayProduct[];
          quantities?: Record<string, number>;
          checkoutSettings?: {
            colors?: {
              backgroundColor?: string;
              buttonColor?: string;
              accentColor?: string;
            };
          } | null;
        };
        setArtistName(data.artistName ?? "");
        setPaymentStatus(data.payment?.status);
        setPaymentAmount(data.payment?.amount ?? 0);
        setProducts(data.products ?? []);
        setQuantities(data.quantities ?? {});
        const cs = data.checkoutSettings ?? null;
        setLocalCheckoutSettings(cs);
        if (cs) setCheckoutSettings(cs);
      } catch (e) {
        console.error("Payment fetch error:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [paymentId, setCheckoutSettings]);

  const backgroundColor = checkoutSettings?.colors?.backgroundColor ?? "#ffffff";
  const textColor = getContrastTextColor(backgroundColor);

  const handleCancel = async () => {
    if (!paymentId) return;
    setCancelError(null);
    setCancelling(true);
    try {
      const res = await fetch(`/api/payments/${paymentId}`, { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancelError(typeof body.error === "string" ? body.error : "Cancel failed");
        return;
      }
      setPaymentStatus("cancelled");
      setCancelDialogOpen(false);
      toast.success("Your order has been cancelled.");
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  if (!paymentId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ color: "#000" }}>
        Missing payment ID
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ color: "#000" }}>
        Loading...
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ color: "#000" }}>
        Payment not found
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor }}>
      <div
        className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
        style={{ color: textColor }}
      >
        <h1 className="text-2xl font-bold text-center">{artistName}</h1>

        <div className="flex-1 mt-6">
          {products.length > 0 ? (
            <OrderQuantitySelector
              products={products}
              quantities={quantities}
              onQuantityChange={noopQuantity}
              total={paymentAmount}
              readOnly
            />
          ) : (
            <div className="text-center text-sm opacity-80 py-8 space-y-2">
              <p>No purchased products on this payment.</p>
              {paymentAmount > 0 ? (
                <p className="font-semibold text-lg">Total: ${formatCentsAsUsd(paymentAmount)}</p>
              ) : null}
            </div>
          )}

          {paymentStatus === "uncaptured" ? (
            <>
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                  disabled={cancelling}
                  onClick={() => {
                    setCancelError(null);
                    setCancelDialogOpen(true);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <Dialog
                open={cancelDialogOpen}
                onOpenChange={(open) => {
                  if (cancelling) return;
                  setCancelDialogOpen(open);
                  if (!open) setCancelError(null);
                }}
              >
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Cancel this order?</DialogTitle>
                    <DialogDescription>
                      Your held payment will be released and this order will not be completed. You can
                      place a new order later if you change your mind.
                    </DialogDescription>
                  </DialogHeader>
                  {cancelError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                  ) : null}
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={cancelling}
                      onClick={() => setCancelDialogOpen(false)}
                    >
                      Keep order
                    </Button>
                    <Button
                      type="button"
                      className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                      disabled={cancelling}
                      onClick={handleCancel}
                    >
                      {cancelling ? "Cancelling..." : "Yes, cancel order"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
